import {
    parseNumber,
    receiveBoolean,
    receiveNumber,
    receiveObject,
    receiveString,
} from "../../../../controllers/adapters/receives"

// `parseArray` solo normaliza `undefined`; una relación sin elementos puede
// venir como `null`, así que se fuerza a array antes de mapear.
const comoArray = (value: any): any[] => (Array.isArray(value) ? value : [])
const EmpresasAdapters = {

    // GET /integra-web/empresas/autorizaciones/disponibles
    // Respuesta del backend:
    //   axios res -> data = { status, message, code, data: PaginaBackendResponse }
    //   PaginaBackendResponse = { elementos[], numeroElementos, numeroPagina(0-based),
    //                             numeroTotalElementos, totalPages, numberOfElements, first, last, empty }
    GET_DISPONIBLES: {

        receive: {

            schema: function (item: any = {}) {

                return {
                    id: receiveNumber(item.id),
                    codigo: receiveString(item.codigo),
                    nombre: receiveString(item.nombre),
                    cif: receiveString(item.identificador_fiscal),
                    email: receiveString(item.email),
                    estado: receiveString(item.estado),
                    autorizada: receiveBoolean(item.estado === 'autorizada'),
                    puede_solicitar: receiveBoolean(item.puede_solicitar),
                }
            },

            adapter: function (data: any) {

                // data = respuesta axios -> data.data = { status, message, code, data }
                // data.data.data  = PaginaBackendResponse
                const paginator = data?.data?.data

                const items = (paginator?.elementos || []).map((item: any) => this.schema(item))

                return {
                    items,
                    numeroElementos: receiveNumber(paginator?.numeroElementos),
                    numeroPagina: receiveNumber(paginator?.numeroPagina),
                    numeroTotalElementos: receiveNumber(paginator?.numeroTotalElementos),
                    totalPages: receiveNumber(paginator?.totalPages),
                    numberOfElements: receiveNumber(paginator?.numberOfElements),
                    first: receiveBoolean(paginator?.first),
                    last: receiveBoolean(paginator?.last),
                    empty: receiveBoolean(paginator?.empty),
                }
            }
        },
    },

    // GET /integra-web/empresas/autorizaciones/buscar-empresa
    // Búsqueda de empresa (por CIF) usada en el modal
    GET_BUSCAR_EMPRESA: {

        receive: {

            schema: function (item: any = {}) {

                return {
                    id: receiveNumber(item.id),
                    codigo: receiveString(item.codigo),
                    nombre: receiveString(item.nombre),
                    cif: receiveString(item.identificador_fiscal),
                    sector: receiveString(item.sector),
                    direccion: receiveString(item.direccion),
                    poblacion: receiveString(item.poblacion),
                    telefono: receiveString(item.telefono),
                    email: receiveString(item.email),
                    contacto: receiveString(item.contacto),
                    estado: receiveString(item.estado),
                    autorizada: receiveBoolean(item.estado === 'autorizada'),
                    puede_solicitar: receiveBoolean(item.puede_solicitar),
                }
            },

            adapter: function (data: any) {

                // Admite tanto PaginaBackendResponse (data.data.data.elementos)
                // como una respuesta con un array directamente en data.data.data
                const paginator = data?.data?.data

                const elementos = paginator?.elementos || (Array.isArray(paginator) ? paginator : [])

                const items = (elementos || []).map((item: any) => this.schema(item))

                return {
                    items,
                    numeroElementos: receiveNumber(paginator?.numeroElementos),
                    numeroPagina: receiveNumber(paginator?.numeroPagina),
                    numeroTotalElementos: receiveNumber(paginator?.numeroTotalElementos),
                    totalPages: receiveNumber(paginator?.totalPages),
                    numberOfElements: receiveNumber(paginator?.numberOfElements),
                    first: receiveBoolean(paginator?.first),
                    last: receiveBoolean(paginator?.last),
                    empty: receiveBoolean(paginator?.empty),
                }
            }
        },
    },

    // GET /integra-web/empresas/mi-acceso
    // Acceso del usuario de la sesión al módulo de empresas. Devuelve si tiene alguna
    // empresa con acceso autorizado, para que el menú decida si muestra el enlace.
    // Respuesta: { status, message, code, data: { tiene_empresas: bool } }
    GET_MI_ACCESO: {

        receive: {

            schema: function (item: any = {}) {

                return {
                    status: receiveBoolean(item?.status),
                    message: receiveString(item?.message),
                    tiene_empresas: receiveBoolean(item?.data?.tiene_empresas),
                }
            },

            adapter: function (data: any) {

                // data = respuesta axios -> data.data = { status, message, code, data }
                return this.schema(data?.data)
            }
        },
    },

    // GET /integra-web/empresas/consultar/{id}
    // Detalle de una empresa. El modelo `Empresa` serializa `identificador_fiscal`.
    GET_EMPRESA: {

        receive: {

            schema: function (item: any = {}) {

                return {
                    id: receiveNumber(item.id),
                    codigo: receiveString(item.codigo),
                    cif: receiveString(item.identificador_fiscal),
                    discriminador: receiveString(item.discriminador),
                    // `dom_validada`, `si_dom` y `siarc` se editan solo desde el panel de
                    // administración, así que "Mis datos" no los necesita.
                    nombre: receiveString(item.nombre),
                    telefono: receiveString(item.telefono),
                    email: receiveString(item.email),
                    persona_nombre: receiveString(item.persona_nombre),
                    persona_apellidos: receiveString(item.persona_apellidos),
                    activar_seguimiento: receiveBoolean(item.activar_seguimiento),
                    fecha_seguimiento: receiveString(item.fecha_seguimiento),
                    habilitado: receiveBoolean(item.habilitado),
                    // Relaciones asignadas a la empresa. El backend las eager-loads
                    // en el detalle; solo se necesita el `id` para el selector
                    // múltiple. El texto se resuelve contra el catálogo de
                    // maestros, porque los modelos no llevan `nombre` (está en la
                    // tabla de textosJOIN).
                    categorias: comoArray(item.categorias).map((item: any) => ({
                        id: receiveNumber(item?.id),
                        nombre: receiveString(item?.nombre),
                    })),
                    departamentos: comoArray(item.departamentos).map((item: any) => ({
                        id: receiveNumber(item?.id),
                        nombre: receiveString(item?.nombre),
                    })),
                }
            },

            adapter: function (data: any) {

                const empresa = data?.data?.data

                return {
                    // Campos normalizados para uso interno (breadcrumb, cif, ...)
                    ...this.schema(empresa),
                    // Objeto tal cual llega del endpoint (incluye los datos de la
                    // persona física). Se envía así en `inscribir_personas_actividad`
                    // para que el WS pueda registrar o actualizar el cliente.
                    raw: empresa || {},
                }
            }
        },
    },

    // POST /integra-web/empresas/autorizaciones/solicitar
    // Request body: { empresa_id }
    // Respuesta:    { status: true, message, code, data }
    POST_SOLICITAR: {

        send: {

            schema: function (item: any = {}) {

                return {
                    empresa_id: parseNumber(item.empresa_id),
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                return {
                    status: receiveBoolean(item?.status),
                    message: receiveString(item?.message),
                    data: receiveObject(item?.data),
                }
            },

            adapter: function (data: any) {

                const dataTemp = data?.data

                const res = this.schema(dataTemp)

                return res
            }
        },
    },

    // POST /integra-web/empresas/registrar
    // Alta de empresa para cuando el buscador no la encuentra. El backend crea la
    // empresa con su persona de contacto y deja la solicitud de acceso del usuario de
    // la sesión en estado pendiente, que es el mismo punto en que queda una empresa que
    // sí aparece en la búsqueda.
    //
    // El `send.schema` es una lista blanca: solo salen los campos del formulario. Los
    // que gestiona el sistema o el panel de administración (`codigo`,
    // `fecha_seguimiento`, `discriminador`, `habilitado`, `activar_seguimiento`,
    // `sincronizado`, `desactivada`, `dom_validada`, `si_dom`, `siarc`) no se envían ni
    // aunque vinieran, porque el backend los rechaza con 422 (`prohibited`).
    POST_REGISTRAR_EMPRESA: {

        send: {

            schema: function (item: any = {}) {

                return {
                    // Datos de la empresa (`integra_clientes`).
                    identificador_fiscal: receiveString(item.identificador_fiscal),
                    nombre: receiveString(item.nombre),
                    telefono: receiveString(item.telefono),
                    email: receiveString(item.email),

                    // Persona de contacto (`integra_personas_fisicas`, con el CIF como
                    // NIF). El prefijo `persona_` es el mismo que usa el alta: evita
                    // colisionar con los campos de la empresa.
                    persona_nombre: receiveString(item.persona_nombre),
                    persona_apellidos: receiveString(item.persona_apellidos),
                    persona_fecha_nacimiento: receiveString(item.persona_fecha_nacimiento),
                    persona_direccion: receiveString(item.persona_direccion),
                    persona_poblacion: receiveString(item.persona_poblacion),
                    persona_codigo_postal: receiveString(item.persona_codigo_postal),
                    persona_provincia: receiveString(item.persona_provincia),
                    persona_comarca: receiveString(item.persona_comarca),
                    persona_pais: receiveString(item.persona_pais),
                    persona_telefono: receiveString(item.persona_telefono),
                    persona_telefono2: receiveString(item.persona_telefono2),
                    persona_telefono_movil: receiveString(item.persona_telefono_movil),
                    persona_telefono_fax: receiveString(item.persona_telefono_fax),
                    persona_email: receiveString(item.persona_email),
                    persona_web: receiveString(item.persona_web),

                    // Relaciones: el backend las sincroniza al dar de alta la empresa,
                    // así que se envía la lista completa de ids.
                    categorias: comoArray(item.categorias).map((id: any) => parseNumber(id)),
                    departamentos: comoArray(item.departamentos).map((id: any) => parseNumber(id)),
                }
            },

            adapter: function (data: any) {

                return {
                    ...this.schema(data)
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                const empresa = item?.data?.empresa
                const autorizacion = item?.data?.autorizacion

                return {
                    status: receiveBoolean(item?.status),
                    message: receiveString(item?.message),
                    empresa: {
                        id: receiveNumber(empresa?.id),
                        codigo: receiveString(empresa?.codigo),
                        cif: receiveString(empresa?.identificador_fiscal),
                        nombre: receiveString(empresa?.nombre),
                        email: receiveString(empresa?.email),
                    },
                    // El alta y la solicitud van en la misma respuesta, así que se
                    // mapean las dos: quien registra ve de inmediato en qué estado ha
                    // quedado su acceso.
                    autorizacion: {
                        estado: receiveString(autorizacion?.estado),
                        origen: receiveString(autorizacion?.origen),
                        autorizada: receiveBoolean(autorizacion?.estado === 'autorizada'),
                    }
                }
            },

            adapter: function (data: any) {

                // data = respuesta axios -> data.data = { status, message, code, data }
                return this.schema(data?.data)
            }
        },
    },

    // PATCH /integra-web/empresas/datos/{id}
    // Guarda los datos de la empresa editables desde el backoffice. `nombre` no
    // se envía nunca: la razón social la mantiene el panel de administración y el
    // backend lo rechaza con 422 (`prohibited`) si llega en el body. Igual con
    // `dom_validada`, `si_dom` y `siarc`, que también son solo del panel de
    // administración.
    UPDATE_DATOS_EMPRESA: {

        send: {

            schema: function (item: any = {}) {

                return {
                    telefono: receiveString(item.telefono),
                    email: receiveString(item.email),
                    persona_nombre: receiveString(item.persona_nombre),
                    persona_apellidos: receiveString(item.persona_apellidos),
                    // Relaciones: el backend las sincroniza, así que se envía la
                    // lista completa de ids (vacía = quitar todas).
                    categorias: comoArray(item.categorias).map((id: any) => parseNumber(id)),
                    departamentos: comoArray(item.departamentos).map((id: any) => parseNumber(id)),
                }
            },

            adapter: function (data: any) {

                return {
                    ...this.schema(data)
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                const empresa = item?.data

                return {
                    status: receiveBoolean(item?.status),
                    message: receiveString(item?.message),
                    // El endpoint devuelve la empresa ya guardada: se mapea con los
                    // mismos campos que el detalle para refrescar el formulario.
                    empresa: {
                        nombre: receiveString(empresa?.nombre),
                        cif: receiveString(empresa?.identificador_fiscal),
                        telefono: receiveString(empresa?.telefono),
                        email: receiveString(empresa?.email),
                        persona_nombre: receiveString(empresa?.persona_nombre),
                        persona_apellidos: receiveString(empresa?.persona_apellidos),
                        // El backend devuelve la empresa con las relaciones ya
                        // sincronizadas, así que se puede refrescar el selector.
                        categorias: comoArray(empresa?.categorias).map((item: any) => receiveNumber(item?.id)),
                        departamentos: comoArray(empresa?.departamentos).map((item: any) => receiveNumber(item?.id)),
                    }
                }
            },

            adapter: function (data: any) {

                // data = respuesta axios -> data.data = { status, message, code, data }
                return this.schema(data?.data)
            }
        },
    },

    // POST {REACT_APP_WEB_SERVICE_ACTIVIDADES}/integraWeb.php  accion=comprobar-empresa
    // Acción del WS d'Activitats (no es la API REST del backoffice), por eso la
    // usa `useIntegraWeb` y no `useAxios`. El WS lee el cuerpo JSON
    // (file_get_contents('php://input')), igual que `inscribir_personas_actividad`.
    //
    // `empresa` se envía con el objeto tal cual llega de
    // `/integra-web/empresas/consultar/{id}` (el `raw` que expone el adaptador),
    // que es la data completa: columnas de `integra_clientes`, `persona_fisica` y
    // las relaciones de categorías y departamentos.
    POST_COMPROBAR_EMPRESA: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: 'comprobar-empresa',
                    empresa_id: parseNumber(item.empresa_id),
                    cif: receiveString(item.cif),
                    empresa: item.empresa && typeof item.empresa === 'object' ? item.empresa : {},
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                return {
                    success: receiveBoolean(item?.success),
                    message: receiveString(item?.message),
                    // El WS es el que decide qué devuelve, así que se conserva la
                    // respuesta tal cual en `raw` para no perder ningún campo
                    // cuando se defina el comportamiento en el front.
                    raw: receiveObject(item)
                }
            },

            adapter: function (data: any) {

                return this.schema(data?.data ?? data)
            }
        },
    },
}

export {
    EmpresasAdapters,
}